import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2 } from "react-icons/fi";
import { AppModal, AppModalBody, AppModalFooter } from "../../components/common/AppModal";
import {
  DetailGrid,
  DetailItem,
  ViewDetailModal,
} from "../../components/common/ViewDetailModal";
import {
  createProjectRequest,
  deleteProjectRequest,
  getProjectRequest,
  listProjectsRequest,
  updateProjectRequest,
} from "../../services/projectsApi";
import Can from "../../components/auth/Can";
import TableRowActions from "../../components/common/TableRowActions";
import { PERMISSIONS } from "../../constants/permissions";
import { usePermissions } from "../../hooks/usePermissions";

function AllProjectsPage() {
  const { modulePermissions } = usePermissions();
  const perms = modulePermissions("project");
  const showActions = perms.canRead || perms.canUpdate || perms.canDelete;
  const tableColCount = showActions ? 5 : 4;
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const perPage = 10;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [viewingProject, setViewingProject] = useState(null);
  const [deleteProjectId, setDeleteProjectId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    slug: "",
    is_active: true,
    description: "",
  });

  const normalizeProject = (project) => ({
    id: project?.id,
    name: project?.name || "",
    slug: project?.slug || "",
    description: project?.description || "",
    is_active: Boolean(project?.is_active ?? true),
    created_at: (project?.created_at || "-").slice(0, 10),
    updated_at: (project?.updated_at || "-").slice(0, 10),
  });

  const extractProjects = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.projects)) return payload.projects;
    if (Array.isArray(payload?.data?.projects)) return payload.data.projects;
    return [];
  };

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      const response = await listProjectsRequest({
        search: search.trim() || undefined,
        page,
        per_page: perPage,
      });
      const payload = response.data;
      const list = extractProjects(payload).map(normalizeProject);
      setProjects(list);

      const fallbackTotal = list.length;
      const resolvedTotal =
        payload?.total ??
        payload?.meta?.total ??
        payload?.data?.total ??
        payload?.data?.meta?.total ??
        fallbackTotal;
      const resolvedLastPage =
        payload?.last_page ??
        payload?.meta?.last_page ??
        payload?.data?.last_page ??
        payload?.data?.meta?.last_page ??
        Math.max(1, Math.ceil(resolvedTotal / perPage));

      setTotalRecords(Number(resolvedTotal) || 0);
      setTotalPages(Number(resolvedLastPage) || 1);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load projects.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, [page, search]);

  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const openCreateModal = () => {
    setEditingProject(null);
    setForm({
      name: "",
      slug: "",
      is_active: true,
      description: "",
    });
    setIsModalOpen(true);
  };

  const openEditModal = async (project) => {
    setEditingProject(project);
    setIsModalOpen(true);
    setEditingProject(project);
    try {
      const response = await getProjectRequest(project.id);
      const payload = response.data?.data || response.data;
      const normalized = normalizeProject(payload);
      setForm({
        name: normalized.name,
        slug: normalized.slug,
        is_active: normalized.is_active,
        description: normalized.description,
      });
    } catch {
      setForm({
        name: project.name,
        slug: project.slug,
        is_active: project.is_active,
        description: project.description,
      });
    }
  };

  const openViewModal = async (project) => {
    try {
      const response = await getProjectRequest(project.id);
      const payload = response.data?.data || response.data;
      setViewingProject(normalizeProject(payload));
    } catch {
      setViewingProject(project);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProject(null);
    setForm({ name: "", slug: "", is_active: true, description: "" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setIsSubmitting(true);
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        description: form.description.trim() || null,
        is_active: form.is_active ? 1 : 0,
      };
      if (editingProject?.id) {
        await updateProjectRequest(editingProject.id, payload);
        toast.success("Project updated successfully.");
      } else {
        await createProjectRequest(payload);
        toast.success("Project created successfully.");
      }
      await loadProjects();
      closeModal();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          (editingProject ? "Failed to update project." : "Failed to create project."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteProjectId) return;
    try {
      await deleteProjectRequest(deleteProjectId);
      toast.success("Project deleted.");
      setDeleteProjectId(null);
      await loadProjects();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete project.");
    }
  };

  return (
    <main className="w-full min-w-0 bg-base-100">
      <section className="w-full border-b border-base-200 bg-base-100">
        <div className="border-b border-base-200 px-4 py-8 sm:px-8">
          <h1 className="ui-page-title tracking-tight">Projects</h1>
          <p className="ui-muted mt-2">
            Create, read, update, and archive project records from one place.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-8">
          <label className="input input-bordered relative flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-xl border-base-300 bg-white pl-4 pr-3 sm:max-w-xl">
            <FiSearch className="h-4 w-4 shrink-0 text-base-content/45" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              className="grow bg-transparent outline-none placeholder:text-base-content/40 ui-nav-text"
              placeholder="Search projects..."
              autoComplete="off"
            />
          </label>
          <Can permission={PERMISSIONS.PROJECT_CREATE}>
            <button type="button" className="ui-create-btn" onClick={openCreateModal}>
              <FiPlus className="ui-create-btn-icon" />
              Create project
            </button>
          </Can>
        </div>

        <div className="overflow-x-auto border-t border-base-200 px-4 sm:px-8">
          <table className="table table-zebra table-pin-rows table-md min-w-[860px] w-full">
            <thead className="border-b border-base-300 bg-base-200/70">
              <tr>
                <th scope="col" className="w-14 ui-table-header">
                  ID
                </th>
                <th scope="col" className="min-w-[12rem] ui-table-header">
                  NAME
                </th>
                <th scope="col" className="min-w-[10rem] ui-table-header">
                  SLUG
                </th>
                <th scope="col" className="min-w-[8rem] ui-table-header whitespace-nowrap">
                  CREATED AT
                </th>
                {showActions ? (
                  <th scope="col" className="ui-table-header ui-table-actions-col">
                    ACTIONS
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, index) => (
                    <tr key={`skeleton-${index}`}>
                      {Array.from({ length: tableColCount }).map((__, innerIndex) => (
                        <td key={`cell-${innerIndex}`}>
                          <div className="skeleton my-2 h-4 w-full rounded-md" />
                        </td>
                      ))}
                    </tr>
                  ))
                : projects.map((project) => (
                    <tr key={project.id}>
                      <td className="tabular-nums text-base-content/70 ui-nav-text">{project.id}</td>
                      <td className="ui-nav-text font-semibold text-base-content">{project.name}</td>
                      <td className="ui-nav-text text-base-content/85">{project.slug}</td>
                      <td className="tabular-nums text-base-content/80 ui-nav-text">{project.created_at}</td>
                      {showActions ? (
                        <td className="ui-table-actions-col">
                          <TableRowActions
                            readPermission={PERMISSIONS.PROJECT_READ}
                            updatePermission={PERMISSIONS.PROJECT_UPDATE}
                            deletePermission={PERMISSIONS.PROJECT_DELETE}
                            onView={() => openViewModal(project)}
                            onEdit={() => openEditModal(project)}
                            onDelete={() => setDeleteProjectId(project.id)}
                            viewLabel={`View ${project.name}`}
                            editLabel={`Edit ${project.name}`}
                            deleteLabel={`Delete ${project.name}`}
                          />
                        </td>
                      ) : null}
                    </tr>
                  ))}
              {!isLoading && projects.length === 0 ? (
                <tr>
                  <td colSpan={tableColCount} className="py-14 align-middle">
                    <div className="flex w-full items-center justify-center">
                      <div className="alert inline-flex rounded-xl bg-base-200/70 px-6">
                        <span className="ui-nav-text text-center">No projects found.</span>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-base-200 px-4 py-4 sm:px-8">
          <p className="ui-meta">
            Showing {totalRecords === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
            {Math.min(page * perPage, totalRecords)} of {totalRecords} entries
          </p>
          <div className="join">
            <button
              type="button"
              className="join-item btn btn-sm rounded-l-lg"
              disabled={page === 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }).map((_, index) => {
              const current = index + 1;
              return (
                <button
                  key={current}
                  type="button"
                  className={`join-item btn btn-sm ${page === current ? "btn-active" : ""}`}
                  onClick={() => setPage(current)}
                >
                  {current}
                </button>
              );
            })}
            <button
              type="button"
              className="join-item btn btn-sm rounded-r-lg"
              disabled={page === totalPages}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      <AppModal
        open={isModalOpen}
        onClose={closeModal}
        title={editingProject ? "Update Project" : "Create Project"}
        subtitle={
          editingProject
            ? "Edit project details and save your changes."
            : "Add project details and save."
        }
      >
        <form className="flex min-h-0 flex-1 flex-col" onSubmit={handleSubmit}>
          <AppModalBody>
              <div className="form-control">
                <label className="label">
                  <span className="label-text ui-label-form">
                    Project Name <span className="text-error">*</span>
                  </span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder="Project name"
                  className="input input-bordered w-full rounded-xl"
                  required
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text ui-label-form">
                    Slug <span className="text-error">*</span>
                  </span>
                </label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(event) => setForm((prev) => ({ ...prev, slug: event.target.value }))}
                  placeholder="Slug (e.g. website-redesign)"
                  className="input input-bordered w-full rounded-xl"
                  required
                />
              </div>
              <label className="label cursor-pointer justify-start gap-3">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, is_active: event.target.checked }))
                  }
                  className="checkbox checkbox-primary checkbox-sm"
                />
                <span className="label-text ui-nav-text">Active project</span>
              </label>
              <textarea
                value={form.description}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, description: event.target.value }))
                }
                placeholder="Description"
                className="textarea textarea-bordered min-h-[120px] w-full rounded-xl"
              />
          </AppModalBody>
          <AppModalFooter>
            <button type="button" className="btn btn-ghost" onClick={closeModal}>
              Cancel
            </button>
            <button
              type="submit"
              className={`btn btn-primary ${isSubmitting ? "btn-disabled" : ""}`}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? editingProject
                  ? "Updating..."
                  : "Creating..."
                : editingProject
                  ? "Update Project"
                  : "Create Project"}
            </button>
          </AppModalFooter>
        </form>
      </AppModal>

      <ViewDetailModal
        open={!!viewingProject}
        onClose={() => setViewingProject(null)}
        title={viewingProject?.name || "Project"}
        subtitle="Project profile"
        meta={
          viewingProject ? (
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`badge badge-sm ${viewingProject.is_active ? "badge-success" : "badge-neutral"}`}
              >
                {viewingProject.is_active ? "Active" : "Inactive"}
              </span>
              <span className="font-mono text-[11px] text-base-content/50">ID {viewingProject.id}</span>
            </div>
          ) : null
        }
      >
        {viewingProject ? (
          <DetailGrid>
            <DetailItem label="Slug" value={viewingProject.slug} />
            <DetailItem label="Created" value={viewingProject.created_at} />
            <DetailItem label="Updated" value={viewingProject.updated_at} />
            <DetailItem label="Description" span={2} value={viewingProject.description || "-"} />
          </DetailGrid>
        ) : null}
      </ViewDetailModal>

      <AppModal
        open={!!deleteProjectId}
        onClose={() => setDeleteProjectId(null)}
        title="Delete Project"
        subtitle="Are you sure you want to delete this project?"
        size="sm"
      >
        <AppModalBody className="py-1">
          <p className="text-xs text-base-content/65">This action cannot be undone.</p>
        </AppModalBody>
        <AppModalFooter>
          <button type="button" className="btn btn-ghost" onClick={() => setDeleteProjectId(null)}>
            Cancel
          </button>
          <button type="button" className="btn btn-error" onClick={handleDelete}>
            Delete
          </button>
        </AppModalFooter>
      </AppModal>

    </main>
  );
}

export default AllProjectsPage;
